import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const MONTHLY_PROPERTY_KEYS = ['Primeira mensalidade', 'Preço das demais mensalidades'];
const CART_ITEM_COUPON_PROPERTY = 'Codigo do cupom';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number | null} */
  #autoSyncTimer = null;

  /** @type {((event: Event) => void) | null} */
  #boundAutoSync = null;

  connectedCallback() {
    super.connectedCallback();
    this.#boundAutoSync = () => {
      this.#scheduleAutoSyncCartCoupons();
    };
    document.addEventListener('cart:update', this.#boundAutoSync);
    this.#scheduleAutoSyncCartCoupons();
  }

  disconnectedCallback() {
    if (this.#boundAutoSync) {
      document.removeEventListener('cart:update', this.#boundAutoSync);
      this.#boundAutoSync = null;
    }
    if (this.#autoSyncTimer) {
      window.clearTimeout(this.#autoSyncTimer);
      this.#autoSyncTimer = null;
    }
    super.disconnectedCallback();
  }

  #scheduleAutoSyncCartCoupons() {
    if (this.#autoSyncTimer) {
      window.clearTimeout(this.#autoSyncTimer);
    }
    this.#autoSyncTimer = window.setTimeout(() => {
      this.#autoSyncTimer = null;
      this.#autoSyncCartCoupons();
    }, 450);
  }

  async #autoSyncCartCoupons() {
    const celsoCupom = globalThis.CelsoCupom;
    if (
      !celsoCupom ||
      typeof celsoCupom.resolveApplicableShopifyCodeForVariantId !== 'function' ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const cart = await this.#fetchCart(abortController.signal);
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        return;
      }

      const candidates =
        typeof celsoCupom.getCouponCandidates === 'function' ? celsoCupom.getCouponCandidates() || [] : [];

      /** @type {string[]} */
      const resolvedCodes = [];

      for (const item of cart.items) {
        if (!item) continue;
        const hint =
          typeof item.properties?.[CART_ITEM_COUPON_PROPERTY] === 'string'
            ? item.properties[CART_ITEM_COUPON_PROPERTY].trim()
            : '';
        const resolvedCode = await celsoCupom.resolveApplicableShopifyCodeForVariantId(item.variant_id, candidates, {
          hint,
          signal: abortController.signal,
        });
        if (resolvedCode) {
          resolvedCodes.push(this.#normalizeDiscountCode(resolvedCode));
        }
      }

      const uniqueCodes = Array.from(new Set(resolvedCodes)).filter(Boolean);
      if (!uniqueCodes.length) return;

      const existingDiscounts = this.#existingDiscounts().map((code) => this.#normalizeDiscountCode(code));
      const alreadyApplied =
        uniqueCodes.length === existingDiscounts.length &&
        uniqueCodes.every((code) => existingDiscounts.includes(code));

      if (alreadyApplied) return;

      const discountPayload = uniqueCodes.join(',');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: discountPayload,
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) return;

      const data = await response.json();
      const allApplicable = uniqueCodes.every((code) =>
        data.discount_codes?.some(
          (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
            this.#normalizeDiscountCode(discount.code) === code && discount.applicable === true
        )
      );

      if (!allApplicable) return;

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml === 'string' && newHtml) {
        morphSection(this.dataset.sectionId, newHtml);
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('cart-discount: falha ao sincronizar cupom do carrinho', error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  }

  #normalizeDiscountCode(value) {
    if (typeof value !== 'string') return '';
    return value.trim().toUpperCase();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #clearDiscountInputIfEditable(input) {
    if (input.readOnly) return;
    input.value = '';
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      const discountCodeValue = this.#normalizeDiscountCode(discountCode.value);
      if (!discountCodeValue) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const resolvedDiscount = await this.#resolveDiscountCode(discountCodeValue, abortController.signal);
      if (!resolvedDiscount) {
        this.#clearDiscountInputIfEditable(discountCode);
        this.#handleDiscountError('discount_code');
        return;
      }

      const { code: resolvedDiscountCode, cart, variantPercentages } = resolvedDiscount;

      if (existingDiscounts.includes(resolvedDiscountCode)) {
        this.#clearDiscountInputIfEditable(discountCode);
        return;
      }

      if (cart && variantPercentages) {
        await this.#applyLineItemProperties({
          cart,
          variantPercentages,
          signal: abortController.signal,
        });
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, resolvedDiscountCode].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === resolvedDiscountCode && discount.applicable === false;
        })
      ) {
        this.#clearDiscountInputIfEditable(discountCode);
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === resolvedDiscountCode && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          this.#clearDiscountInputIfEditable(discountCode);
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  async #resolveDiscountCode(baseCode, signal) {
    const shopDomain = this.dataset.shopDomain || window.Shopify?.shop;
    const findDiscountUrl = this.dataset.findDiscountUrl || '/apps/coupon/find-discount';

    if (!shopDomain) return null;

    const cart = await this.#fetchCart(signal);
    if (!cart || !Array.isArray(cart.items)) return null;

    const variantIds = cart.items
      .map((item) => item?.variant_id)
      .filter((variantId) => typeof variantId === 'number' || typeof variantId === 'string')
      .map((variantId) => `gid://shopify/ProductVariant/${variantId}`);

    if (variantIds.length === 0) return null;

    const resolvedViaFindDiscount = await this.#resolveDiscountViaFindDiscount({
      baseCode,
      shopDomain,
      findDiscountUrl,
      variantIds,
      cart,
      signal,
    });
    if (resolvedViaFindDiscount) return resolvedViaFindDiscount;

    return this.#resolveDiscountViaAppLookup(baseCode, cart, signal);
  }

  async #resolveDiscountViaFindDiscount({ baseCode, shopDomain, findDiscountUrl, variantIds, cart, signal }) {
    if (!findDiscountUrl) return null;

    try {
      const response = await fetch(findDiscountUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: baseCode,
          shop: shopDomain,
          variantIds: Array.from(new Set(variantIds)),
        }),
        signal,
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (!data?.success || !data.code) return null;

      return {
        code: data.code,
        cart,
        variantPercentages: data.variantPercentages || null,
      };
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return null;
    }
  }

  async #resolveDiscountViaAppLookup(baseCode, cart, signal) {
    const lookupUrl = `/apps/discount?code=${encodeURIComponent(baseCode)}`;

    try {
      const response = await fetch(lookupUrl, { signal });
      if (!response.ok) return null;

      const data = await response.json();
      if (!data?.success || !data.code) return null;

      return {
        code: data.code,
        cart,
        variantPercentages: null,
      };
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return null;
    }
  }

  async #applyLineItemProperties({ cart, variantPercentages, signal }) {
    if (!cart || !Array.isArray(cart.items) || !variantPercentages) {
      return;
    }

    for (let index = 0; index < cart.items.length; index += 1) {
      const item = cart.items[index];
      const variantGid = this.#variantIdToGid(item?.variant_id);
      if (!variantGid) continue;

      const percentages = variantPercentages[variantGid];
      if (!percentages) continue;

      const propertyValues = this.#buildMonthlyProperties(item, percentages);
      if (!propertyValues) continue;

      const mergedProperties = this.#mergeProperties(item?.properties, propertyValues);
      if (!mergedProperties) continue;

      const payload = {
        line: index + 1,
        quantity: item?.quantity ?? 1,
        properties: mergedProperties,
      };

      try {
        await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', { body: JSON.stringify(payload) }),
          signal,
        });
      } catch (error) {
        console.warn('Não foi possível atualizar propriedades do item do carrinho:', error);
      }
    }
  }

  #variantIdToGid(variantId) {
    if (typeof variantId === 'string') {
      return variantId.startsWith('gid://') ? variantId : `gid://shopify/ProductVariant/${variantId}`;
    }

    if (typeof variantId === 'number') {
      return `gid://shopify/ProductVariant/${variantId}`;
    }

    return null;
  }

  #buildMonthlyProperties(item, percentages) {
    if (!item || !percentages) {
      return null;
    }

    const basePriceCents = this.#resolveBasePriceCents(item);
    if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) {
      return null;
    }

    /** @type {Record<string, string>} */
    const properties = {};

    const primeiraMensalidade = this.#calculateDiscountedPrice(basePriceCents, percentages.porcentagem);
    if (typeof primeiraMensalidade === 'number') {
      properties['Primeira mensalidade'] = this.#formatCurrency(primeiraMensalidade);
    }

    const demaisMensalidades = this.#calculateDiscountedPrice(basePriceCents, percentages.porcentagemDemais);
    if (typeof demaisMensalidades === 'number') {
      properties['Preço das demais mensalidades'] = this.#formatCurrency(demaisMensalidades);
    }

    return Object.keys(properties).length > 0 ? properties : null;
  }

  #resolveBasePriceCents(item) {
    const compareAt = Number(item?.variant_compare_at_price ?? item?.compare_at_price ?? 0);
    const price = Number(item?.price ?? 0);
    const normalizedCompareAt = Number.isFinite(compareAt) && compareAt > 0 ? compareAt : 0;
    const normalizedPrice = Number.isFinite(price) && price > 0 ? price : 0;

    if (normalizedCompareAt > normalizedPrice) {
      return normalizedCompareAt;
    }

    if (normalizedPrice > 0) {
      return normalizedPrice;
    }

    // Fallback defensivo para itens antigos sem `price`.
    const original = Number(item?.original_price ?? 0);
    if (!Number.isFinite(original) || original <= 0) {
      return null;
    }
    return original;
  }

  #calculateDiscountedPrice(basePriceCents, percentage) {
    if (!Number.isFinite(basePriceCents) || basePriceCents <= 0) {
      return null;
    }

    if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 1) {
      return null;
    }

    const discounted = Math.round(basePriceCents * (1 - percentage));
    return discounted >= 0 ? discounted : null;
  }

  #formatCurrency(valueInCents) {
    if (!Number.isFinite(valueInCents)) {
      return '';
    }

    const amount = valueInCents / 100;
    const locale = window.Shopify?.locale || 'pt-BR';
    const currency = window.Shopify?.currency?.active || 'BRL';

    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

  #mergeProperties(existing, incoming) {
    const current = existing && typeof existing === 'object' ? { ...existing } : {};
    let didChange = false;

    for (const [key, value] of Object.entries(incoming)) {
      if (!value) continue;
      const normalizedValue = typeof value === 'string' ? value : String(value);

      // Se a mensalidade já foi calculada no fluxo de PDP/tarja e persistida no item,
      // mantemos esse valor para evitar divergência visual (ex.: 95,83 na tarja e 97,43 no carrinho).
      if (MONTHLY_PROPERTY_KEYS.includes(key)) {
        const existingMonthlyValue = current[key];
        if (typeof existingMonthlyValue === 'string' && existingMonthlyValue.trim() !== '') {
          continue;
        }
      }

      if (current[key] === normalizedValue) {
        continue;
      }

      current[key] = normalizedValue;
      didChange = true;
    }

    return didChange ? current : null;
  }

  async #clearMonthlyProperties(signal) {
    const cart = await this.#fetchCart(signal);
    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    for (let index = 0; index < cart.items.length; index += 1) {
      const item = cart.items[index];
      if (!item || !item.properties) {
        continue;
      }

      const hasMonthlyProperties = MONTHLY_PROPERTY_KEYS.some((key) => {
        const value = item.properties[key];
        return typeof value === 'string' && value.trim() !== '';
      });

      if (!hasMonthlyProperties) {
        continue;
      }

      const updatedProperties = { ...item.properties };
      MONTHLY_PROPERTY_KEYS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(updatedProperties, key)) {
          updatedProperties[key] = '';
        }
      });

      try {
        await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              line: index + 1,
              quantity: item?.quantity ?? 1,
              properties: updatedProperties,
            }),
          }),
          signal,
        });
      } catch (error) {
        console.warn('Não foi possível remover propriedades de mensalidades:', error);
      }
    }
  }

  async #fetchCart(signal) {
    const cartUrl = Theme.routes.cart_url.endsWith('.js')
      ? Theme.routes.cart_url
      : `${Theme.routes.cart_url}.js`;

    const response = await fetch(cartUrl, { signal });
    if (!response.ok) return null;

    return response.json();
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  /**
   * Remove all discount codes (cupom exibido no input + pills ocultas).
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscountFromInput = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const codes = this.#existingDiscounts();
    const discountInput = this.querySelector('input[name="discount"]');
    const hasCouponInInput =
      discountInput instanceof HTMLInputElement &&
      discountInput.readOnly &&
      discountInput.value.trim().length > 0;
    if (codes.length === 0 && !hasCouponInInput) return;

    const abortController = this.#createAbortController();

    try {
      try {
        await this.#clearMonthlyProperties(abortController.signal);
      } catch (error) {
        console.warn('Falha ao limpar propriedades de mensalidades:', error);
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: '', sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        /* resposta sem JSON */
      }

      if (!response.ok) {
        console.warn('cart-discount: falha ao remover cupom', response.status, data);
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      const sectionHtml = data.sections?.[this.dataset.sectionId];
      if (sectionHtml) {
        morphSection(this.dataset.sectionId, sectionHtml);
      }
    } catch (error) {
      console.warn('cart-discount: erro ao remover cupom', error);
    } finally {
      this.#activeFetch = null;
    }
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      try {
        await this.#clearMonthlyProperties(abortController.signal);
      } catch (error) {
        console.warn('Falha ao limpar propriedades de mensalidades:', error);
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill[data-discount-code]');
    for (const pill of discountPills) {
      if (pill instanceof Element) {
        const code = pill.getAttribute('data-discount-code');
        if (typeof code === 'string' && code.trim() !== '') {
          discountCodes.push(code.trim());
        }
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
